import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackChangeListClick, trackMenuItemClick } from './tracking';
import { toggleChangeListActive } from '../../actions/chatList';
import { EInternetStatus } from '../../actions/internetStatus';
import { changeRoute } from '../../actions/route';
import { TopMenuDesktop } from '../../components/TopMenuDesktop';
import { TopMenuMobile } from '../../components/TopMenuMobile';
import {
  chatListSelector,
  internetStatusSelector,
  isGroupActionsEnabledSelector,
  masterFiltersSelector,
  userIdSelector,
  userSelector,
} from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { EDeviceType } from '../../types/deviceType';
import { EMasterTabs } from '../../types/masterFilters';
import { TRoute } from '../../types/route';
import { ETopMenuStatus } from '../../types/topMenuStatus';

type TTopMenuContainerProps = {
  isChangeListVisible: boolean;
};

export const TopMenuContainer: React.VFC<TTopMenuContainerProps> = ({ isChangeListVisible }) => {
  const dispatch = useDispatch();
  const { currentRoute, isChangeListActive, selectedChats, status } = useSelector(chatListSelector);
  const internetStatus = useSelector(internetStatusSelector);
  const deviceType = useDeviceType();
  const { isAuthenticated } = useSelector(userSelector);
  const isGroupActionsEnabled = useSelector(isGroupActionsEnabledSelector);
  const { activeTab, selectedSubAgentId } = useSelector(masterFiltersSelector);
  const userId = useSelector(userIdSelector);

  const onChangeRoute = React.useCallback(
    (route: TRoute) => {
      const opponentId = activeTab === EMasterTabs.SubAgentsChats && selectedSubAgentId ? selectedSubAgentId : userId;

      dispatch(changeRoute({ route }));
      trackMenuItemClick(route, opponentId);
    },
    [activeTab, dispatch, selectedSubAgentId, userId],
  );

  const onChangeListClick = React.useCallback(() => {
    dispatch(toggleChangeListActive());

    if (!isChangeListActive) {
      trackChangeListClick();
    }
  }, [dispatch, isChangeListActive]);

  const topMenuStatus =
    internetStatus === EInternetStatus.Offline
      ? ETopMenuStatus.Offline
      : status === ERequestApiStatus.Pending
      ? ETopMenuStatus.Loading
      : isChangeListActive
      ? ETopMenuStatus.ChangeList
      : !isAuthenticated
      ? ETopMenuStatus.NotAuthorized
      : ETopMenuStatus.Default;

  return deviceType === EDeviceType.Desktop ? (
    <TopMenuDesktop
      currentRoute={currentRoute}
      onChangeRoute={onChangeRoute}
      onChangeListClick={onChangeListClick}
      isChangeListVisible={isGroupActionsEnabled && isChangeListVisible}
      selectedCount={selectedChats.length}
      status={topMenuStatus}
    />
  ) : (
    <TopMenuMobile
      currentRoute={currentRoute}
      onChangeRoute={onChangeRoute}
      onChangeListClick={onChangeListClick}
      isChangeListVisible={isGroupActionsEnabled && isChangeListVisible}
      selectedCount={selectedChats.length}
      status={topMenuStatus}
    />
  );
};
